'use client';

import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { BTN_GLOW_SM } from '@/constants/button-classes';
import { LOCAL_STORAGE_COOKIES_ACCEPTED } from '@/constants/local-storage';
import { cn } from '@/utils/cn';
import { COOKIE_NOTICE_SEEN_EVENT, hasSeenCookieNotice, markCookieNoticeSeen } from '@/utils/consent';

/**
 * Cookie notice banner (SCRUM-1954, `docs/cookie-consent.md`) — informational only: tracking is already
 * running, so there is a single "Accept" that records the notice as seen (`npCookiesAccepted='true'`)
 * and hides the banner. It shows on every page until that click.
 *
 * `public/affiliates-sdk.min.js` draws its own `#cookie-consent` banner on init whenever its flag is not
 * `'true'`, and on every path change re-labels whatever `#cookie-consent` exists. So ours has its own id,
 * and the SDK's banner is dropped the moment it is appended.
 */

const BANNER_ID = 'np-cookie-consent';
const LEGACY_BANNER_SELECTOR = '#cookie-consent';
const TITLE_ID = 'np-cookie-consent-title';
const MESSAGE_ID = 'np-cookie-consent-message';

/**
 * Marks `<body>` while the banner is on screen. `global.css` hides the chat launchers under it: the
 * bubble sits on the banner's corner and swallows pointer and keyboard focus — worst in RTL/mobile,
 * where the banner wraps under it. Intercom: only the launcher, an open conversation stays. Kang: the
 * whole widget steps aside and comes back intact when the banner closes.
 */
export const CONSENT_OPEN_ATTRIBUTE = 'data-np-consent-open';

/**
 * 44px target. Merged with `cn` because it overrides the base button's padding, `whitespace-nowrap`
 * (long tr/ru/fr labels at 320px / 200% zoom) and, on the glow, `border-0`.
 */
const CONSENT_BUTTON = 'min-h-11 w-full px-5 tracking-[0.04em] whitespace-normal text-center mobile:w-auto lg:min-w-48 rtl:tracking-normal';

/** The chat widgets that sit on the banner's corner. `inert` is what removes their click and focus, where `content-visibility` is not supported. */
const CHAT_WIDGET_SELECTOR = "#noping-kang, iframe[src*='kang.noping.com']";

// Not `useLayoutEffect` on the server (it would warn): the banner only exists after mount anyway.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const setChatWidgetsInert = (isInert: boolean) => {
  document.querySelectorAll(CHAT_WIDGET_SELECTOR).forEach((widget) => widget.toggleAttribute('inert', isInert));
};

const removeLegacyBanner = () => {
  document.querySelectorAll(LEGACY_BANNER_SELECTOR).forEach((legacyBanner) => legacyBanner.remove());
};

// Compact variant (home nova only, see CookieConsent's `variant`). Same section, ids, ARIA, single "Accept", policy link,
// <body> mark and chat `inert` as the site banner — only the look changes: a thin strip, always on screen until the
// visitor accepts, even if it covers the bottom edge of a block. It sits under the header and its menus (header z-50,
// strip z-40).

/**
 * Reserves the height of the compact strip while it is on screen: a padding-bottom on <body> closes the page (so the end
 * of the footer can be scrolled above it) and scroll-padding-bottom keeps what gets keyboard focus or an anchor scroll out
 * from under it. Padding, not a spacer element: `_app` mounts the banner before the page (first in the Tab order), so a
 * spacer pushed the header down instead of closing the page. Both go away with the strip. Measured from a
 * ResizeObserver, whose first callback comes right after the next layout, so nothing forces a layout in the hydration;
 * window resizes re-measure too.
 */
function useCompactStripReserve(stripRef: RefObject<HTMLElement>, enabled: boolean): void {
  useEffect(() => {
    const strip = stripRef.current;
    if (!enabled || !strip) return undefined;
    const root = document.documentElement;
    const { body } = document;
    const previous = root.style.scrollPaddingBottom;
    const previousBodyPadding = body.style.paddingBottom;
    const measure = () => {
      root.style.scrollPaddingBottom = `${strip.offsetHeight}px`;
      body.style.paddingBottom = `${strip.offsetHeight}px`;
    };
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(strip);
    else measure();
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      // `removeProperty` when there was nothing before: assigning '' to a padding longhand is ignored by jsdom.
      if (previous) root.style.scrollPaddingBottom = previous;
      else root.style.removeProperty('scroll-padding-bottom');
      if (previousBodyPadding) body.style.paddingBottom = previousBodyPadding;
      else body.style.removeProperty('padding-bottom');
    };
  }, [stripRef, enabled]);
}

interface CookieConsentProps {
  /**
   * `compact`: thin strip always visible at the bottom of the screen until the visitor answers (home nova; `_app` picks
   * it by route). `default`: the site banner, unchanged everywhere else. Both record the notice the same way.
   */
  variant?: 'default' | 'compact';
}

export function CookieConsent({ variant = 'default' }: CookieConsentProps) {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const [isMessageScrollable, setIsMessageScrollable] = useState(false);
  const messageRef = useRef<HTMLParagraphElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const isCompact = variant === 'compact';
  useCompactStripReserve(sectionRef, isVisible && isCompact);

  useEffect(() => {
    if (!hasSeenCookieNotice()) setIsVisible(true);

    // Closes on the support chat opening (same tab) and on the choice made in another tab.
    const hide = () => setIsVisible(false);
    const handleStorage = (event: StorageEvent) => {
      // `key === null` is a `localStorage.clear()`: no record left, so nothing to close.
      if (event.key === LOCAL_STORAGE_COOKIES_ACCEPTED && hasSeenCookieNotice()) hide();
    };
    window.addEventListener(COOKIE_NOTICE_SEEN_EVENT, hide);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(COOKIE_NOTICE_SEEN_EVENT, hide);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // The SDK appends its banner to <body> once, on init, which can land before or long after we mount
  // (`afterInteractive`). Watching <body>'s direct children catches it either way, before it paints.
  // The SDK only draws it while `npCookiesAccepted !== 'true'`, so once the notice was seen there is nothing to watch for.
  useEffect(() => {
    removeLegacyBanner();
    if (hasSeenCookieNotice()) return undefined;
    const observer = new MutationObserver(removeLegacyBanner);
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, []);

  // A layout effect, so the attribute lands in the same commit that shows the banner: with a passive effect
  // one frame paints the banner with the chat launcher still on top of it.
  useIsomorphicLayoutEffect(() => {
    if (!isVisible) return undefined;
    document.body.setAttribute(CONSENT_OPEN_ATTRIBUTE, '');
    return () => document.body.removeAttribute(CONSENT_OPEN_ATTRIBUTE);
  }, [isVisible]);

  // While the banner is up the chat widgets are inert: no click, no focus. Kang can be appended after we
  // mount, so <body>'s direct children are watched (only while visible; `kang.js` attaches its host there, and a
  // subtree observer would re-run the query on every DOM change of an animated page), and the attribute is lifted on cleanup.
  useEffect(() => {
    if (!isVisible) return undefined;
    const applyInert = () => setChatWidgetsInert(true);
    applyInert();
    const observer = new MutationObserver(applyInert);
    observer.observe(document.body, { childList: true });
    return () => {
      observer.disconnect();
      setChatWidgetsInert(false);
    };
  }, [isVisible]);

  // The message scrolls inside the banner's 60dvh cap (zoom, landscape); only then does it need a Tab
  // stop to be scrolled from the keyboard — otherwise the button stays the first stop.
  useEffect(() => {
    const message = messageRef.current;
    if (!isVisible || !message || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setIsMessageScrollable(message.scrollHeight > message.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(message);
    return () => observer.disconnect();
  }, [isVisible]);

  const accept = () => {
    markCookieNoticeSeen();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  if (isCompact) {
    return (
      <section ref={sectionRef} id={BANNER_ID} aria-labelledby={TITLE_ID} aria-describedby={MESSAGE_ID} data-variant='compact' className='fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[rgba(11,15,18,0.94)] pb-[env(safe-area-inset-bottom)] backdrop-blur-xl'>
        <div className='mx-auto flex w-full max-w-[1400px] items-center gap-x-5 gap-y-1.5 px-5 py-2 max-[767px]:flex-wrap max-[767px]:px-3 max-[767px]:py-1.5'>
          <h2 id={TITLE_ID} className='sr-only'>
            {t('cookie_consent.title')}
          </h2>
          <p id={MESSAGE_ID} className='min-w-0 flex-1 text-start text-[13px] leading-[1.35] text-[var(--np-fg-dim)] max-[767px]:basis-full max-[767px]:text-[12px]'>
            <Trans t={t} i18nKey='cookie_consent.message' components={{ policy: <Link href='/terms/privacy-policy' className='underline underline-offset-2 hover:text-white' /> }} />
          </p>
          {/* Same lane reserve as the site banner from lg up: the chat bubble's corner stays free. */}
          <div className='flex shrink-0 items-center max-[767px]:ms-auto lg:me-[5.5rem]'>
            <button type='button' onClick={accept} className={cn(BTN_GLOW_SM, 'min-h-8 max-w-full border border-transparent !py-1 !text-[12px] max-[767px]:whitespace-normal max-[767px]:text-center')}>
              {t('cookie_consent.accept')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} id={BANNER_ID} aria-labelledby={TITLE_ID} aria-describedby={MESSAGE_ID} className='fixed inset-x-0 bottom-0 z-[1000] flex max-h-[60dvh] flex-col border-t border-white/10 bg-[rgba(11,15,18,0.92)] pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur-xl lg:pb-[max(1.25rem,env(safe-area-inset-bottom))]'>
      {/* Same container as the redesigned pages: 1400px max width. Below the modals (z-[1001]): the banner is not modal. */}
      <div className='relative mx-auto flex min-h-0 w-full max-w-[1400px] flex-1 flex-col items-stretch gap-4 px-4 pt-4 lg:flex-row lg:items-center lg:gap-8 lg:px-5 lg:pt-5'>
        <h2 id={TITLE_ID} className='sr-only'>
          {t('cookie_consent.title')}
        </h2>
        {/* The message scrolls, not the section, so the buttons never leave the viewport. A scrolling region
            must be focusable to be scrolled from the keyboard (WCAG 2.1.1, axe `scrollable-region-focusable`). */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <p ref={messageRef} id={MESSAGE_ID} tabIndex={isMessageScrollable ? 0 : undefined} className='min-h-0 min-w-0 overflow-y-auto overscroll-contain text-start text-sm leading-[1.6] text-[var(--np-fg-dim)] lg:flex-1'>
          {/* The tag is `<policy>`, never `<link>`: the translation parser treats `<link>` as a void HTML element, so the anchor rendered empty with its text left outside it. The colour is explicit because the global `a` rule paints links blue (~2.9:1 on this dark surface). */}
          <Trans t={t} i18nKey='cookie_consent.message' components={{ policy: <Link href='/terms/privacy-policy' className='rounded-sm font-semibold text-[var(--np-fg)] underline decoration-white/40 underline-offset-[3px] transition-colors hover:text-[var(--np-green)] hover:decoration-current focus-visible:text-[var(--np-green)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--np-green)]' /> }} />
        </p>
        {/* Keeps the button out of the chat bubble's lane (bottom corner, 88px, never mirrored): the end of the row
            from lg up, and the physical right in RTL between 478 and 1023px, where the row start is the right.
            Under 478px the button is full width and the CSS hiding + `inert` are what clear the bubble. */}
        <div className='flex w-full shrink-0 flex-col mobile:flex-row mobile:items-center lg:me-[5.5rem] lg:w-auto mobile:max-lg:rtl:pr-[5.5rem]'>
          <button type='button' onClick={accept} className={cn(BTN_GLOW_SM, CONSENT_BUTTON, 'border border-transparent')}>
            {t('cookie_consent.accept')}
          </button>
        </div>
      </div>
    </section>
  );
}
